import { QueryClientProvider, type QueryClient } from '@tanstack/preact-query';
import type { ComponentChildren } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { AnalyticsProvider } from '../analytics/AnalyticsProvider';
import type { WidgetBuildInfo } from '../analytics/types';
import { ApiClientContext, type ArcticApiClient } from '../arcticapi';
import { ConsentProvider } from '../consent/ConsentProvider';
import {
  DEFAULT_WIDGET_CONSENT_STATE,
  type WidgetConsentState,
} from '../consent/consentState';
import {
  TenantWebsiteBaseHrefContext,
  TimetableNotesEnabledContext,
} from '../config/TenantContext';
import {
  getTenantWebsiteBaseHref,
  isTimetableNotesEnabled,
} from '../config/tenantConfig';
import { WidgetSettingsContext } from '../config/WidgetSettingsContext';
import type { WidgetInitialConfig } from '../widgetConfig';
import {
  createWidgetApiClient,
  createWidgetQueryClient,
} from './widgetRuntime';

interface WidgetProvidersProps {
  hostElement: HTMLElement;
  tenant: string;
  baseUrl?: string;
  initialConfig?: WidgetInitialConfig;
  consentState?: WidgetConsentState;
  buildInfo?: WidgetBuildInfo;
  children: ComponentChildren;
}

const DEFAULT_WIDGET_INITIAL_CONFIG: WidgetInitialConfig = {
  initialTab: 'plan',
  initialOrigin: '',
  initialDestination: '',
  departureStopId: '',
  disableMyLocation: false,
};

const DEFAULT_WIDGET_BUILD_INFO: WidgetBuildInfo = {
  timestamp: '',
  version: '',
  commitSha: '',
};

interface WidgetRuntimeProviderProps {
  client: ArcticApiClient;
  queryClient: QueryClient;
  children: ComponentChildren;
}

export function WidgetRuntimeProvider({
  client,
  queryClient,
  children,
}: WidgetRuntimeProviderProps) {
  return (
    <ApiClientContext.Provider value={client}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ApiClientContext.Provider>
  );
}

export function WidgetProviders({
  hostElement,
  tenant,
  baseUrl,
  initialConfig = DEFAULT_WIDGET_INITIAL_CONFIG,
  consentState = DEFAULT_WIDGET_CONSENT_STATE,
  buildInfo = DEFAULT_WIDGET_BUILD_INFO,
  children,
}: WidgetProvidersProps) {
  const tenantWebsiteBaseHref = useMemo(
    () => getTenantWebsiteBaseHref(tenant),
    [tenant],
  );
  const timetableNotesEnabled = useMemo(
    () => isTimetableNotesEnabled(tenant),
    [tenant],
  );
  const client = useMemo(
    () => createWidgetApiClient({ tenant, baseUrl }),
    [tenant, baseUrl],
  );

  const [queryClient] = useState(createWidgetQueryClient);

  return (
    <ConsentProvider hostElement={hostElement} consentState={consentState}>
      <AnalyticsProvider
        hostElement={hostElement}
        tenant={tenant}
        initialConfig={initialConfig}
        buildInfo={buildInfo}
      >
        <TenantWebsiteBaseHrefContext.Provider value={tenantWebsiteBaseHref}>
          <TimetableNotesEnabledContext.Provider value={timetableNotesEnabled}>
            <WidgetSettingsContext.Provider
              value={{ disableMyLocation: initialConfig.disableMyLocation }}
            >
              <WidgetRuntimeProvider client={client} queryClient={queryClient}>
                {children}
              </WidgetRuntimeProvider>
            </WidgetSettingsContext.Provider>
          </TimetableNotesEnabledContext.Provider>
        </TenantWebsiteBaseHrefContext.Provider>
      </AnalyticsProvider>
    </ConsentProvider>
  );
}
